import { Box, type HTMLChakraProps } from '@chakra-ui/react'
import type { Node as NodeType } from '@xyflow/react'
import { type FC, memo, useCallback, useEffect, useRef, useState } from 'react'

import { useColorModeValue } from '@app/components/ui/color-mode'
import useGetNode from '@app/hooks/useGetNode'
import RichInput from '@app/next/forms/richInput'
import NodeResizer from '@app/pages/strategies/components/nodes/components/nodeResizer'
import Can from '@app/shared/authorization/can'
import { Card } from '@app/shared/cards/components'
import MarkdownDisplay from '@app/shared/markdownDisplay'
import { useStore } from '@app/store'

const NoteNode: FC<NodeType> = (rfNode) => {
  const { id, selected: nodeSelected } = rfNode

  const selected = nodeSelected === undefined ? false : nodeSelected
  const node = useGetNode(id)

  const nodeRef = useRef(null)
  const notesRef = useRef(null)

  const updateObject = useStore.use.updateObject()

  const [editingWithoutRef, setEditingWithoutRef] = useState(false)
  const editingRef = useRef(editingWithoutRef)
  const editing = editingRef.current
  const setEditing = (data: boolean) => {
    editingRef.current = data
    setEditingWithoutRef(data)
  }

  const backgroundColorWithoutEditing = useColorModeValue('yellow.50', 'yellow.500')
  const borderColorWithoutEditing = useColorModeValue('yellow.400', 'yellow.700')

  // used to focus the input when the node is double clicked and place the caret at the end
  // const focusInputAndPlaceCaretAtEnd = (element: HTMLElement) => {
  //   element.focus()
  //   const range = document.createRange()
  //   range.selectNodeContents(element)
  //   range.collapse(false)
  //   const sel = window.getSelection()
  //   sel.removeAllRanges()
  //   sel.addRange(range)
  // }

  const toggleEditMode = useCallback((enabled: boolean) => {
    setEditing(enabled)
  }, [])

  const saveChanges = useCallback(
    (text: string) => {
      toggleEditMode(false)

      if (notesRef.current) {
        updateObject({ note: { id: node.data.id, text } })
      }
    },
    [node?.data?.id, toggleEditMode, updateObject]
  )

  const handleInputBlur = (event: KeyboardEventInit) => {
    if (event.key === 'Enter' && !event.shiftKey && editingRef.current) {
      const textAreaValue =
        nodeRef.current && (nodeRef.current as HTMLElement).getElementsByTagName('textarea')[0].value

      saveChanges(textAreaValue)
    }
  }

  // useEffect(() => {
  //   const editor = document.getElementsByClassName('public-DraftEditor-content')[0]
  //
  //   if (editor) {
  //     focusInputAndPlaceCaretAtEnd(editor as HTMLElement)
  //   }
  // }, [editing])

  useEffect(() => {
    document.addEventListener('keydown', handleInputBlur)

    return () => {
      document.removeEventListener('keydown', handleInputBlur)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useEffect(() => {
    if (!selected && editing) {
      const textAreaValue =
        nodeRef.current && (nodeRef.current as HTMLElement).getElementsByTagName('textarea')[0].value

      saveChanges(textAreaValue)
    }
  }, [editing, id, selected, toggleEditMode, notesRef, saveChanges])

  if (!node || !node.data) {
    return null
  }

  const { data: note, height, width } = node

  const backgroundColor = !editing ? backgroundColorWithoutEditing : 'transparent'
  const clearStyleOnEditMode = editing ? { boxShadow: 'none', padding: 0 } : {}

  const borderWidth = '2px'
  const borderColor = selected && !editing ? 'blue.500' : borderColorWithoutEditing
  // const hoverBorder = !editing && { borderColor: 'blue.500' }
  const placeholderText = 'Double click to edit'
  const widthPadding = 53

  const cardStyle = {
    backgroundColor,
    style: clearStyleOnEditMode,
    borderWidth,
    borderColor,
    // _hover: hoverBorder,
    width,
    height,
    borderRadius: 0,
    boxShadow: 'lg'
  }
  const markdownStyle: HTMLChakraProps<'div'> = {
    pos: 'absolute',
    px: 4,
    py: 2,
    maxW: width - widthPadding,
    opacity: note.text?.length > 0 ? 1 : 0.5,
    visibility: editing ? 'hidden' : 'visible',
    color: '#333'
  }

  return (
    <>
      <Can I="update" a="strategy">
        {!editing && (
          <NodeResizer strategyId={node.strategyId} nodeId={id} isVisible={selected} baseZoom={15} maxZoom={25} />
        )}
      </Can>
      <Card
        onClick={() => {}}
        onDoubleClick={() => {
          toggleEditMode(true)
        }}
        {...cardStyle}
      >
        <Box ref={notesRef} {...markdownStyle}>
          <MarkdownDisplay text={note.text === '' ? placeholderText : note.text} />
        </Box>
        {editing && (
          <Box border="2px" borderColor="blue.500" borderRadius="sm">
            <Box
              ref={nodeRef}
              color="black"
              border="5px"
              borderColor="yellow.100"
              borderRadius="sm"
              bgColor="yellow.50"
            >
              <RichInput name="text" defaultValue={note.text || ''} _hover={{ cursor: 'text' }} className="nodrag" />
            </Box>
          </Box>
        )}
      </Card>
    </>
  )
}

export default memo(NoteNode)
